import { decode } from '../../flexPolyline';

const avoidFeatureMapping = {
    'seasonalClosure':false,
    'tollRoad': false,
    'controlledAccessHighway': false,
    'ferry': false,
    'carShuttleTrain': false,
    'tunnel': false,
    'dirtRoad': false,
    'difficultTurns': false,
    'uTurns': false,
}

const advancedViaFields={
    "course":"",
    "sideOfStreetHint(pin)":"",
    "displayLocation(pin)":"",
    "uTurnPermission(pin)":"",
    "matchSideOfStreet(pin)":"",
    "nameHint(pin)":"",
    "radius(pin)":"",
    "snapRadius(pin)":"",
    "minCourseDistance(pin)":"",
    "customizationIndex(pin)":"",
    "stopDuration(pin)":"",
    "radiusPenalty(pin)":"",
    "segmentIdHint(pin)":"",
    "onRoadThreshold(pin)":"",
    "passThrough(pin)":"",
    "charging(pin)":"",
    "currentWeightChange(pin)":"",
    "extraParams": ""
}


function transformToPolygon(encodedPolygon) {
    if (typeof encodedPolygon !== 'string') {
        throw new Error('Encoded polygon must be a string');
    }
    try {
        return decode(encodedPolygon);
    } catch (error) {
        throw new Error(`Failed to decode polygon: ${error.message}`);
    }
}

function decodePolygon(area,type){
    if (Array.isArray(area.geometry) && area.geometry.length === 1 && typeof area.geometry[0] === 'string') {
        area.geometry = transformToPolygon(area.geometry[0]);
        area.type = type;
    }   
}

 function transformArea(area) { 
        const base = {
            name:area.name
        };

        if ( (area.type === 'polygon' || area.type === "encodedPolygon") && Array.isArray(area.geometry)) {
            if(area.type === "encodedPolygon"){
               decodePolygon(area,'polygon');
            }
            base.polygon = area.geometry.map(coord => coord.map(Number));
        }

        if (area.type === 'bbox' && Array.isArray(area.geometry) && area.geometry.length === 2) {
            const [min, max] = area.geometry;
            base.boundingBox = {
                minLat: Math.min(Number(min[0]), Number(max[0])),
                minLon: Math.min(Number(min[1]), Number(max[1])),
                maxLat: Math.max(Number(min[0]), Number(max[0])),
                maxLon: Math.max(Number(min[1]), Number(max[1])),
            };
        }

        if (area.type === 'corridor' && Array.isArray(area.geometry)) {
            if(area.type === "encodedCorridor"){
               decodePolygon(area,'corridor');
            }

            base.corridor = {};
            base.corridor.centerLine = area.geometry.map(coord => coord.map(Number));
            base.corridor.radius = area.radius ? Number(area.radius) : 200;
        }

        if (area.type === 'exceptionCorridor' && Array.isArray(area.geometry)) {
             if(area.type === "encodedCorridor"){
               decodePolygon(area,'exceptionCorridor');
            }
            base.exceptionCorridor = {};
            base.exceptionCorridor.centerLine = area.geometry.map(coord => coord.map(Number));
            base.exceptionCorridor.radius = area.radius ? Number(area.radius) : 200;
        }
       
        return base;
    }

function transformAreasWithExceptions(areas) {
    if (!Array.isArray(areas) || areas.length === 0) return [];

    // Group areas by their parent index (i)
    const groupedByParent = {};
    areas.forEach((area) => {
        const { i } = area;
        if (!groupedByParent[i]) {
            groupedByParent[i] = { parent: null, exceptions: [] };
        }
        if (!area.isException) {
            groupedByParent[i].parent = area;
        } else {
            groupedByParent[i].exceptions.push(area);
        }
    });

    // Build payload with nested exceptions
    const result = [];
    Object.keys(groupedByParent).forEach((parentIndex) => {
        const { parent, exceptions } = groupedByParent[parentIndex];
        if (!parent) return;

        const transformed = transformArea(parent);

        // Nest exceptions under their parent area by type
        if (exceptions.length > 0) {
            exceptions.forEach(exc => {
                const geometry = Array.isArray(exc.geometry) ? exc.geometry.map(coord => coord.map(Number)) : [];
                if (exc.type === 'corridor' || exc.type === 'encodedCorridor') {
                    transformed.exceptionCorridor = {
                        centerLine: geometry,
                        radius: exc.radius ? Number(exc.radius) : 200
                    };
                } else if (exc.type === 'polygon' || exc.type === 'encodedPolygon') {
                    transformed.exceptionPolygon = {
                        outer: geometry
                    };
                } else if (exc.type === 'bbox' && geometry.length === 2) {
                    transformed.exceptionBoundingBox = {
                        minLat: Math.min(Number(geometry[0][0]), Number(geometry[1][0])),
                        minLon: Math.min(Number(geometry[0][1]), Number(geometry[1][1])),
                        maxLat: Math.max(Number(geometry[0][0]), Number(geometry[1][0])),
                        maxLon: Math.max(Number(geometry[0][1]), Number(geometry[1][1]))
                    };
                }
            });
        }

        result.push(transformed);
    });

    return result;
}

function generateCommonRules(avoidFeatures, avoid, vehicleSpeedCap, exclude) {

    let mergedAvoidFeatures = {};
    if (avoidFeatures && avoidFeatures.values) {
        const avoidFeatureState = avoidFeatures.values.map(feature => ({ [feature]: true }));
        mergedAvoidFeatures = Object.assign({}, avoidFeatureMapping, ...avoidFeatureState);
    }

    const speedCaps = {};
    if(typeof vehicleSpeedCap === 'string' && vehicleSpeedCap.length >0  && vehicleSpeedCap.split(',').length > 0) {
        vehicleSpeedCap.split(',').forEach( (item,index) => {
            const counter = index + 1;
            speedCaps[counter] = parseFloat(item.trim());
        });
    }

    const areaRules = {
        excludeAreas: transformAreasWithExceptions(exclude?.areas),
        avoidAreas: transformAreasWithExceptions(avoid?.areas),
    };

    return {
        mergedAvoidFeatures,
        speedCaps,
        areaRules
    };
}

export const createODRulesForApi = function(id, avoidFeatures, avoid, vehicleSpeedCap, exclude, origin, destination, via, name ) {

    const {mergedAvoidFeatures, speedCaps, areaRules} = generateCommonRules(avoidFeatures, avoid, vehicleSpeedCap, exclude);
    
    // Transform origin and destination
    const originCoords = origin.split(',').map(Number);
    const destinationCoords = destination.split(',').map(Number);
    
    // Transform via points
    const viaPoints = via && via.length ? via.map(point => point.coords.split(',').map(Number)).filter(point => point.length === 2) : [];    
    
    return {
        originDestinationRules:{
            [id]: {
                name: name,
                origin: { coordinates: originCoords },
                destination: { coordinates: destinationCoords },
                ...(viaPoints.length > 0 ? { viaPoints: viaPoints } : {}),
                avoidFeature: {
                ...mergedAvoidFeatures
                },
            ...(Object.keys(speedCaps).length > 0 ? { speedCaps: { ...speedCaps } } : {}),
            areaRules: { ...areaRules }
            }
        }
    }
}

export function createGlobalRulesForApi(avoidFeatures, avoid, vehicleSpeedCap, exclude ) {

    const {mergedAvoidFeatures, speedCaps, areaRules} = generateCommonRules(avoidFeatures, avoid, vehicleSpeedCap, exclude);
    
    return {
        globalRules: {
            avoidFeature: {
                ...mergedAvoidFeatures
            },
            ...(Object.keys(speedCaps).length > 0 ? { speedCaps: { ...speedCaps } } : {}),
            areaRules: { ...areaRules }
        }
    };
}

function isLatLngFormat(str) {
    if (!str || typeof str !== 'string') return false;
    const parts = str.split('_');
    if (parts.length !== 2) return false;
    const [latStr, lngStr] = parts;
    if(isNaN(latStr) || isNaN(lngStr)) return false;
    const isValidNumber = (numStr) => /^-?\d+(\.\d+)?$/.test(numStr);
    if (!isValidNumber(latStr) || !isValidNumber(lngStr)) return false;
    return true;  
};


function extractExcludeAreas(areaRules){

    let exclude = {};
    if(areaRules.excludeAreas && Array.isArray(areaRules.excludeAreas) && areaRules.excludeAreas.length > 0 ){
        let excludeAreas = [];
        areaRules.excludeAreas.forEach((area, index) => {
            excludeAreas.push({
                geometry: area.polygon ? area.polygon : area.boundingBox ? [
                    [area.boundingBox.minLat, area.boundingBox.minLon],
                    [area.boundingBox.maxLat, area.boundingBox.maxLon]
                ] : area.corridor ? area.corridor.centerLine : area.exceptionCorridor ? area.exceptionCorridor.centerLine : [],
                i: index,
                type: area.polygon ? 'polygon' : area.boundingBox ? 'bbox' : area.corridor ? 'corridor' : area.exceptionCorridor ? 'exceptionCorridor' : 'unknown',
                isException:false,
                name:area.name,
                isAutoName: isLatLngFormat(area.name),
                ...(area.corridor ? {radius: area.corridor.radius} : area.exceptionCorridor ? {radius: area.exceptionCorridor.radius} : {})
            });

            // Extract nested exceptionCorridor as a separate area entry
            if (area.exceptionCorridor && (area.boundingBox || area.polygon || area.corridor)) {
                const excName = area.exceptionCorridor.centerLine && area.exceptionCorridor.centerLine.length > 0
                    ? `${area.exceptionCorridor.centerLine[0][0]}_${area.exceptionCorridor.centerLine[0][1]}`
                    : 'ExceptionCorridor';
                excludeAreas.push({
                    geometry: area.exceptionCorridor.centerLine || [],
                    i: index,
                    type: 'corridor',
                    isException: true,
                    name: excName,
                    isAutoName: isLatLngFormat(excName),
                    radius: area.exceptionCorridor.radius || 200
                });
            }

            // Extract nested exceptionPolygon as a separate area entry
            if (area.exceptionPolygon && (area.boundingBox || area.polygon || area.corridor)) {
                const outer = area.exceptionPolygon.outer || [];
                const excName = outer.length > 0
                    ? `${outer[0][0]}_${outer[0][1]}`
                    : 'ExceptionPolygon';
                excludeAreas.push({
                    geometry: outer,
                    i: index,
                    type: 'polygon',
                    isException: true,
                    name: excName,
                    isAutoName: isLatLngFormat(excName)
                });
            }

            // Extract nested exceptionBoundingBox as a separate area entry
            if (area.exceptionBoundingBox && (area.boundingBox || area.polygon || area.corridor)) {
                const ebb = area.exceptionBoundingBox;
                const excName = `${ebb.minLat}_${ebb.minLon}`;
                excludeAreas.push({
                    geometry: [
                        [ebb.minLat, ebb.minLon],
                        [ebb.maxLat, ebb.maxLon]
                    ],
                    i: index,
                    type: 'bbox',
                    isException: true,
                    name: excName,
                    isAutoName: isLatLngFormat(excName)
                });
            }
        });
        exclude.areas = excludeAreas;

    }
    else{
        exclude = {
            areas:[]
        };
    }
    return exclude;
}

function extractAvoidAreas(areaRules){
    let avoid = {};
    if(areaRules.avoidAreas && Array.isArray(areaRules.avoidAreas) && areaRules.avoidAreas.length > 0 ){
        let avoidAreas = [];
        areaRules.avoidAreas.forEach((area, index) => {
            avoidAreas.push({
                geometry: area.polygon ? area.polygon : area.boundingBox ? [
                    [area.boundingBox.minLat, area.boundingBox.minLon],
                    [area.boundingBox.maxLat, area.boundingBox.maxLon]
                ] : area.corridor ? area.corridor.centerLine : area.exceptionCorridor ? area.exceptionCorridor.centerLine : [],
                i: index,
                type: area.polygon ? 'polygon' : area.boundingBox ? 'bbox' : area.corridor ? 'corridor' : area.exceptionCorridor ? 'exceptionCorridor' : 'unknown',
                isException:false,
                name:area.name,
                isAutoName: isLatLngFormat(area.name),
                ...(area.corridor ? {radius: area.corridor.radius} : area.exceptionCorridor ? {radius: area.exceptionCorridor.radius} : {})
            });

            // Extract nested exceptionCorridor as a separate area entry
            if (area.exceptionCorridor && (area.boundingBox || area.polygon || area.corridor)) {
                const excName = area.exceptionCorridor.centerLine && area.exceptionCorridor.centerLine.length > 0
                    ? `${area.exceptionCorridor.centerLine[0][0]}_${area.exceptionCorridor.centerLine[0][1]}`
                    : 'ExceptionCorridor';
                avoidAreas.push({
                    geometry: area.exceptionCorridor.centerLine || [],
                    i: index,
                    type: 'corridor',
                    isException: true,
                    name: excName,
                    isAutoName: isLatLngFormat(excName),
                    radius: area.exceptionCorridor.radius || 200
                });
            }

            // Extract nested exceptionPolygon as a separate area entry
            if (area.exceptionPolygon && (area.boundingBox || area.polygon || area.corridor)) {
                const outer = area.exceptionPolygon.outer || [];
                const excName = outer.length > 0
                    ? `${outer[0][0]}_${outer[0][1]}`
                    : 'ExceptionPolygon';
                avoidAreas.push({
                    geometry: outer,
                    i: index,
                    type: 'polygon',
                    isException: true,
                    name: excName,
                    isAutoName: isLatLngFormat(excName)
                });
            }

            // Extract nested exceptionBoundingBox as a separate area entry
            if (area.exceptionBoundingBox && (area.boundingBox || area.polygon || area.corridor)) {
                const ebb = area.exceptionBoundingBox;
                const excName = `${ebb.minLat}_${ebb.minLon}`;
                avoidAreas.push({
                    geometry: [
                        [ebb.minLat, ebb.minLon],
                        [ebb.maxLat, ebb.maxLon]
                    ],
                    i: index,
                    type: 'bbox',
                    isException: true,
                    name: excName,
                    isAutoName: isLatLngFormat(excName)
                });
            }
        });
        avoid.areas = avoidAreas;

    }
    else{
        avoid = {
            areas: []
        };
    }

    return avoid;
}


export function parseGlobalRulesFromApi(globalRulesObj) {
    const { globalRules } = globalRulesObj;


    // Parse avoidFeatures
    const avoidFeature = globalRules.avoidFeature || {};
    const avoidFeatures = {
        values: Object.keys(avoidFeature).filter(key => avoidFeature[key])
    };
    // Parse speedCaps
    const speedCaps = globalRules.speedCaps || {};
    const vehicleSpeedCap = Object.values(speedCaps).join(',');

    // Parse areaRules
    const areaRules = globalRules.areaRules || {};
    const exclude = extractExcludeAreas(areaRules);
    const avoid = extractAvoidAreas(areaRules);
    
    return {
        avoidFeatures,
        avoid,
        vehicleSpeedCap,
        exclude
    };
}


export function parseODRulesFromApi(originDestinationRules){

    const { origin, destination, viaPoints, name } = originDestinationRules;

    const avoidFeature = originDestinationRules.avoidFeature || {};
    const avoidFeatures = {
        values: Object.keys(avoidFeature).filter(key => avoidFeature[key])
    };

    // Parse viaPoints
    const viaPointsObj = Array.isArray(viaPoints) ? viaPoints.map(point => ({ "coords": point.join(','), "advanced": { ...advancedViaFields } })) : [];

    // Parse speedCaps
    const speedCaps = originDestinationRules.speedCaps || {};
    const vehicleSpeedCap = Object.values(speedCaps).join(',');

    const areaRules = originDestinationRules.areaRules || {};
    const exclude = extractExcludeAreas(areaRules);
    const avoid = extractAvoidAreas(areaRules);
    
    return {
        avoidFeatures,
        avoid,
        vehicleSpeedCap,
        exclude,
        origin: origin["coordinates"].join(','),
        destination: destination["coordinates"].join(','),
        viaPoints: viaPointsObj,
        odRuleName: name || ''
    };

}